import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useFeedData } from '../hooks/useFeedData';
import { useCsvData } from '../hooks/useCsvData';
import DatasetSelector from './DatasetSelector';
import DateSelector from './DateSelector';
import DataDisplay from './DataDisplay';
import CsvDataDisplay from './CsvDataDisplay';
import './FeedExplorer.css';

const FeedExplorer: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { data, loading, error, refetch } = useFeedData(slug || '');
  const [selectedDatasetId, setSelectedDatasetId] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  // Auto-select first dataset when data loads
  useEffect(() => {
    if (data && data.datasets.length > 0 && !selectedDatasetId) {
      setSelectedDatasetId(data.datasets[0].id);
    }
  }, [data, selectedDatasetId]);

  // Auto-select most recent date when dataset changes
  useEffect(() => {
    if (data && selectedDatasetId) {
      const dates = data.availableDates[selectedDatasetId];
      if (dates && dates.length > 0) {
        setSelectedDate(dates[0]);
      }
    }
  }, [data, selectedDatasetId]);

  // Get current dataset
  const currentDataset = data?.datasets.find(d => d.id === selectedDatasetId);

  // Get the selected resource URL for CSV data
  const selectedResource = selectedDatasetId && selectedDate && data
    ? data.resourcesByDatasetAndDate[selectedDatasetId]?.[selectedDate]?.[0]
    : null;
  const csvUrl = selectedResource?.url || null;

  // Fetch CSV data for selected resource
  const { data: csvData, loading: csvLoading, error: csvError } = useCsvData(csvUrl);

  if (!slug) {
    return (
      <div className="feed-explorer">
        <div className="error-state">
          <h1>Brak parametru inwestycji</h1>
          <p>Podaj slug inwestycji w URL, np. /baltica-lubkowo</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="feed-explorer">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Pobieranie danych dla inwestycji: <strong>{slug}</strong></p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="feed-explorer">
        <div className="error-state">
          <h1>Błąd podczas pobierania danych</h1>
          <p>{error}</p>
          <button onClick={refetch} className="retry-button">
            🔄 Spróbuj ponownie
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="feed-explorer">
        <div className="error-state">
          <h1>Brak danych</h1>
          <p>Nie udało się pobrać danych dla inwestycji: <strong>{slug}</strong></p>
        </div>
      </div>
    );
  }

  const selectedResources = selectedDatasetId && selectedDate && data
    ? data.resourcesByDatasetAndDate[selectedDatasetId]?.[selectedDate] || []
    : [];

  const availableDates = selectedDatasetId ? data.availableDates[selectedDatasetId] || [] : [];

  // Calculate total resources across all datasets
  const totalResources = data.datasets.reduce((sum, dataset) => sum + dataset.resources.length, 0);

  return (
    <div className="feed-explorer">
      <header className="feed-header">
        <h1>{currentDataset?.title.pl || 'Feed Viewer'}</h1>
        <p className="feed-description">{currentDataset?.description.pl || ''}</p>
      </header>

      <div className="selectors-row">
        <DatasetSelector
          datasets={data.datasets}
          selectedDatasetId={selectedDatasetId}
          onDatasetSelect={setSelectedDatasetId}
        />

        <DateSelector
          availableDates={availableDates}
          selectedDate={selectedDate}
          onDateSelect={setSelectedDate}
        />
      </div>

      {selectedDate && (
        <>
          <DataDisplay
            resources={selectedResources}
            selectedDate={selectedDate}
          />

          <CsvDataDisplay
            csvData={csvData}
            loading={csvLoading}
            error={csvError}
            selectedDate={selectedDate}
          />
        </>
      )}
    </div>
  );
};

export default FeedExplorer;